import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { Subject, Subscription, catchError, debounceTime, distinctUntilChanged, filter, map, of, switchMap, tap } from 'rxjs';
import { Asociado } from '../../../api';
import { AsociadosListadoService } from '../../services/asociados-listado.service';

export const DEBOUNCE_BUSCADOR_MS = 300;

/** Mensaje comprensible para un fallo de la búsqueda (sin detalles internos). */
export function mensajeErrorBusqueda(err: HttpErrorResponse | null | undefined): string {
  if (err?.status === 403) return 'No tienes permiso para buscar asociados.';
  if (err?.status === 401) return 'Tu sesión ha caducado. Vuelve a iniciar sesión.';
  return err?.error?.status?.message || 'No se ha podido buscar. Inténtalo de nuevo.';
}

/**
 * Selector de asociados con BÚSQUEDA REMOTA (GET /asociados?paginado=true): nunca descarga la lista completa ni depende de que
 * el asociado esté en la primera página. Busca por nombre, apellidos, DNI/NIF, teléfono o correo (parámetro `q` de la API).
 *  - debounce de 300 ms y cancelación de la petición anterior (switchMap): no hay una petición por pulsación ni respuestas viejas;
 *  - estados de carga, sin resultados y error (con reintento);
 *  - `conservarSeleccion`: la elección se mantiene como ficha aunque cambien los resultados;
 *  - `idInicial`: recupera por ID (GET /asociados/{id}) una selección ya guardada al abrir un formulario de edición, esté o no en los
 *    primeros resultados.
 * Los permisos los valida la API (si no hay permiso, el error se muestra y no se abre ningún acceso).
 */
@Component({
  selector: 'app-asociado-buscador',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './asociado-buscador.component.html'
})
export class AsociadoBuscadorComponent implements OnInit, OnChanges, OnDestroy {
  /** Control con el texto escrito (lo posee el padre: p. ej. el NIF del Histórico). */
  @Input({ required: true }) control!: FormControl<string | null>;
  @Input() inputId = 'asociado-buscador';
  @Input() placeholder = 'Nombre, apellidos o DNI/NIF';
  @Input() minCaracteres = 2;
  @Input() tamano = 10;
  @Input() asociacion: number | null = null;
  @Input() conservarSeleccion = false;
  @Input() idInicial: number | null = null;

  @Output() elegido = new EventEmitter<Asociado>();
  @Output() limpiado = new EventEmitter<void>();
  /** Enter con el texto escrito: búsqueda exacta por DNI/NIF a cargo del padre. */
  @Output() buscarExacto = new EventEmitter<void>();

  abierto = false;
  cargando = false;
  error = '';
  resultados: Asociado[] = [];
  seleccionado: Asociado | null = null;
  recuperando = false;
  errorRecuperacion = '';

  private readonly reintento$ = new Subject<void>();
  private readonly suscripciones = new Subscription();
  private ultimoTexto = '';
  /** Texto para el que el padre ya pidió la búsqueda exacta (Enter/botón): no se lanza además la búsqueda de sugerencias. */
  private textoOmitido: string | null = null;

  constructor(private readonly listado: AsociadosListadoService, private readonly host: ElementRef<HTMLElement>) {}

  ngOnInit(): void {
    this.suscripciones.add(
      this.control.valueChanges.pipe(
        map(valor => (valor ?? '').trim()),
        debounceTime(DEBOUNCE_BUSCADOR_MS),
        distinctUntilChanged(),
        filter(texto => texto !== this.textoOmitido),
        tap(texto => {
          this.ultimoTexto = texto;
          this.error = '';
          if (texto.length < this.minCaracteres) { this.resultados = []; this.cargando = false; this.abierto = false; return; }
          this.cargando = true; this.abierto = true;
        }),
        switchMap(texto => texto.length < this.minCaracteres ? of(null) : this.buscar(texto))
      ).subscribe(res => this.alRecibir(res))
    );
    this.suscripciones.add(
      this.reintento$.pipe(
        tap(() => { this.error = ''; this.cargando = true; this.abierto = true; }),
        switchMap(() => this.buscar(this.ultimoTexto))
      ).subscribe(res => this.alRecibir(res))
    );
  }

  ngOnChanges(cambios: SimpleChanges): void {
    if (cambios['idInicial']) this.recuperarSeleccion();
  }

  ngOnDestroy(): void { this.suscripciones.unsubscribe(); }

  private buscar(texto: string) {
    return this.listado.buscar({ page: 1, pageSize: this.tamano, q: texto, asociacion: this.asociacion }).pipe(
      map(pagina => ({ asociados: pagina?.asociados ?? [] })),
      catchError((err: HttpErrorResponse) => of({ error: mensajeErrorBusqueda(err) }))
    );
  }

  private alRecibir(res: { asociados?: Asociado[]; error?: string } | null): void {
    this.cargando = false;
    if (!res) return;
    if (res.error) { this.error = res.error; this.resultados = []; return; }
    this.resultados = res.asociados ?? [];
  }

  /** Recupera por ID la selección ya guardada (edición): no depende de la búsqueda ni de la primera página. */
  private recuperarSeleccion(): void {
    this.errorRecuperacion = '';
    if (!this.idInicial) { return; }
    if (this.seleccionado?.id === this.idInicial) return;
    this.recuperando = true;
    this.listado.obtenerPorId(this.idInicial).subscribe({
      next: res => {
        this.recuperando = false;
        const asociado = res?.asociados?.[0];
        if (asociado) { this.seleccionado = asociado; } else { this.errorRecuperacion = 'No se ha encontrado el asociado seleccionado.'; }
      },
      error: (err: HttpErrorResponse) => {
        this.recuperando = false;
        this.errorRecuperacion = err?.status === 404 ? 'El asociado seleccionado ya no existe.' : mensajeErrorBusqueda(err);
      }
    });
  }

  reintentar(): void { this.reintento$.next(); }

  elegir(asociado: Asociado): void {
    if (this.conservarSeleccion) this.seleccionado = asociado;
    this.control.setValue(asociado.nif ?? '', { emitEvent: false });
    this.ultimoTexto = (asociado.nif ?? '').trim();
    this.abierto = false;
    this.elegido.emit(asociado);
  }

  quitarSeleccion(): void {
    this.seleccionado = null;
    this.limpiado.emit();
  }

  cerrar(): void { this.abierto = false; }

  /** El padre resuelve ya el texto actual (búsqueda exacta): cierra la lista y evita una petición de sugerencias redundante. */
  cancelar(): void {
    this.abierto = false;
    this.textoOmitido = (this.control.value ?? '').trim();
  }

  abrir(): void { if (this.ultimoTexto.length >= this.minCaracteres && (this.resultados.length || this.error)) this.abierto = true; }

  @HostListener('document:click', ['$event'])
  clicFuera(evento: Event): void {
    if (this.abierto && !this.host.nativeElement.contains(evento.target as Node)) this.abierto = false;
  }

  nombreCompleto(a: Asociado): string { return [a.apellidos, a.nombre].filter(Boolean).join(', ') || '—'; }
}
